import {
  Card,
  FormControl,
  FormLabel,
  Switch,
  Flex,
  Box,
  Select,
  Button,
  Text,
  Heading
} from '@homebotapp/hb-react-component-catalog'
import {
  BUY_DOWN_MAPPINGS,
  BUY_DOWN_TYPE,
  NO_CLEAR_BUY_DOWN_TYPE_FOUND,
  ONE_ONE,
  ONE_ZERO,
  THREE_TWO_ONE,
  TWO_ONE
} from './BuyDownType'
import { defineMessages, useIntl } from 'react-intl'
import { BuyDownLearnMoreModal } from './BuyDownLearnMoreModal'
import { BuyDownDetailsModal } from './BuyDownDetailsModal'
import { formatCurrencyWithOpts } from '../../../../../../helpers/formatCurrencyWithOpts'
import { calculateMonthlyPayment } from '../../Utilities/EstimatedMonthlyPaymentUtilities'
import { useEffect } from 'react'
import { useCreateClientEvent } from '../../../../../../hooks'
import { ClientEventName, ClientEventSource } from '../../../../../../api/gqlaxy/gql/generated/graphql'

const MSG = defineMessages({
  calculateWithBuyDown: {
    id: 'EstimatedMonthlyPaymentBreakdownBuyDownToggle.calculateWithBuyDown',
    defaultMessage: 'Calculate with a buydown'
  },
  selectBuyDownType: {
    id: 'EstimatedMonthlyPaymentBreakdownBuyDownToggle.selectBuyDownType',
    defaultMessage: 'Select Buydown Type'
  },
  buyDown: {
    id: 'EstimatedMonthlyPaymentBreakdownBuyDownToggle.buyDown',
    defaultMessage: 'buydown'
  },
  year: {
    id: 'EstimatedMonthlyPaymentBreakdownBuyDownToggle.year',
    defaultMessage: 'Year {year}'
  },
  years: {
    id: 'EstimatedMonthlyPaymentBreakdownBuyDownToggle.years',
    defaultMessage: 'Years {year}'
  },
  perMonth: {
    id: 'EstimatedMonthlyPaymentBreakdownBuyDownToggle.perMonth',
    defaultMessage: '/mo'
  },
  buyDownType: {
    id: 'EstimatedMonthlyPaymentBreakdownBuyDownToggle.buyDownType',
    defaultMessage: 'Buydown type'
  }
})

interface EstimatedMonthlyPaymentBreakdownBuyDownToggleProps {
  buyDownToggle: boolean
  buyDownType: BUY_DOWN_TYPE
  listingId?: string
  loanAmount: number
  loanTerm: number
  rate: number
  setAssumableToggle: (value: boolean) => void
  setBuyDownToggle: (value: boolean) => void
  setBuyDownType: (value: BUY_DOWN_TYPE) => void
  setYear: (value: string) => void
  selectedYear: string
}

export const EstimatedMonthlyPaymentBreakdownBuyDownToggle = ({
  buyDownToggle,
  buyDownType,
  listingId,
  loanAmount,
  loanTerm,
  rate,
  setAssumableToggle,
  setBuyDownToggle,
  setBuyDownType,
  setYear,
  selectedYear
}: EstimatedMonthlyPaymentBreakdownBuyDownToggleProps) => {
  const intl = useIntl()
  const { mutateAsync: createClientEvent } = useCreateClientEvent()

  const handleBuyDownToggleOn = (checked: boolean) => {
    setBuyDownToggle(checked)
    if (checked) {
      setAssumableToggle && setAssumableToggle(false)
    }
    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.ToggledBuyDownSwitch,
        eventSource: listingId ? ClientEventSource.ListingDetailed : ClientEventSource.Calculators,
        ...(listingId && { listingId })
      }
    })
  }

  /**
   * Handles changing buy down type that is shorter than the selected year
   */
  useEffect(() => {
    if (Number(selectedYear) > BUY_DOWN_MAPPINGS[buyDownType].length) {
      setYear(BUY_DOWN_MAPPINGS[buyDownType][BUY_DOWN_MAPPINGS[buyDownType].length - 1].year)
    }
  }, [selectedYear, buyDownType])

  return (
    <Card>
      <Flex justify='space-between' align='center' flexDir={'row'} mx={4}>
        <FormControl display='flex' alignItems='center'>
          <Flex align='center' gap={4} my={2}>
            <Switch
              name='buyDownConcession'
              isChecked={buyDownToggle}
              onChange={e => handleBuyDownToggleOn(e.target.checked)}
              tracking={{
                guid: 'kHdgYivv5paJNurtt7VKDf',
                ui_context: 'Listing Detail Buy Down Toggle',
                descriptor: 'Toggled Buy Down Toggle'
              }}
            />
            <FormLabel mb={0} display='flex' alignItems='center'>
              {intl.formatMessage(MSG.calculateWithBuyDown)}
            </FormLabel>
          </Flex>
        </FormControl>
        <BuyDownLearnMoreModal listingId={listingId} />
      </Flex>
      {buyDownToggle && buyDownType && buyDownType !== NO_CLEAR_BUY_DOWN_TYPE_FOUND && (
        <Box m={4}>
          <FormControl>
            <FormLabel>{intl.formatMessage(MSG.buyDownType)}</FormLabel>
            <Select
              name='buyDownType'
              onChange={e => {
                setBuyDownType(e.target.value as BUY_DOWN_TYPE)
                createClientEvent({
                  clientEventInput: {
                    eventName: ClientEventName.SelectedBuyDownType,
                    eventSource: listingId ? ClientEventSource.ListingDetailed : ClientEventSource.Calculators,
                    ...(listingId && { listingId })
                  }
                })
              }}
              value={buyDownType}
              tracking={{
                guid: '9Sue5hoeqzHNA2L7RKWR2q',
                ui_context: 'Listing Detail Buy Down Toggle',
                descriptor: 'Selected Buy Down Type'
              }}
            >
              <option value={THREE_TWO_ONE}>{THREE_TWO_ONE + ' ' + intl.formatMessage(MSG.buyDown)}</option>
              <option value={TWO_ONE}>{TWO_ONE + ' ' + intl.formatMessage(MSG.buyDown)}</option>
              <option value={ONE_ONE}>{ONE_ONE + ' ' + intl.formatMessage(MSG.buyDown)}</option>
              <option value={ONE_ZERO}>{ONE_ZERO + ' ' + intl.formatMessage(MSG.buyDown)}</option>
            </Select>
          </FormControl>
          <Flex
            my={2}
            gap={2}
            overflowX='auto'
            overflowY='hidden'
            css={{
              '&::-webkit-scrollbar': {
                height: '4px'
              },
              '&::-webkit-scrollbar-track': {
                background: 'neutral.100'
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'neutral.300',
                borderRadius: '2px'
              }
            }}
            sx={{
              '@media (min-width: 48em)': {
                flexWrap: 'wrap',
                overflowX: 'visible'
              }
            }}
          >
            {BUY_DOWN_MAPPINGS[buyDownType].map(({ year, rateDrop }, index, array) => (
              <Button
                key={year}
                onClick={() => {
                  setYear(year)
                  createClientEvent({
                    clientEventInput: {
                      eventName: ClientEventName.SelectedBuyDownYear,
                      eventSource: listingId ? ClientEventSource.ListingDetailed : ClientEventSource.Calculators,
                      ...(listingId && { listingId })
                    }
                  })
                }}
                bgColor={year === selectedYear ? 'brand.50' : 'neutral.100'}
                borderColor={year === selectedYear ? 'brand.600' : 'neutral.300'}
                _hover={{
                  bgColor: year === selectedYear ? 'brand.200' : 'neutral.300',
                  borderColor: year === selectedYear ? 'brand.800' : 'neutral.500'
                }}
                h={'auto'}
                minW={'fit-content'}
                tracking={{
                  guid: 'kvJrmsnocCvwpX1GitWfhZ',
                  ui_context: 'Listing Detail Buy Down Toggle',
                  descriptor: 'Selected Buy Down Year'
                }}
              >
                <Flex justify='space-between' align='center' flexDir={'column'}>
                  <Heading size='xs' variant={'eyebrow'} color={'neutral-700'} my={0}>
                    {intl
                      .formatMessage(index === array.length - 1 ? MSG.years : MSG.year, {
                        year: index === array.length - 1 ? `${year}+` : year
                      })
                      .toUpperCase()}
                  </Heading>
                  <Text color={'neutral.900'} fontWeight={'bold'} size={'md'} my={0}>
                    {formatCurrencyWithOpts(calculateMonthlyPayment(loanAmount, rate - rateDrop, loanTerm), {
                      minorDigits: false
                    }) + intl.formatMessage(MSG.perMonth)}
                  </Text>
                  <Text color={'neutral.900'} size={'sm'} mt={0}>
                    {(rate - rateDrop).toFixed(1)}%
                  </Text>
                </Flex>
              </Button>
            ))}
          </Flex>
          <BuyDownDetailsModal
            buyDownType={buyDownType}
            rate={rate}
            loanTerm={loanTerm}
            loanAmount={loanAmount}
            listingId={listingId}
          />
        </Box>
      )}
    </Card>
  )
}
